import EditOutlined from '@mui/icons-material/EditOutlined';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';

interface CreativeNameProps {
  name: string;
  onRename: (name: string) => void;
  editLabel?: string;
  variant?: TypographyProps['variant'];
  nameSx?: SxProps<Theme>;
  fullWidth?: boolean;
}

export function CreativeName({
  name,
  onRename,
  editLabel = 'Rename',
  variant,
  nameSx,
  fullWidth = false,
}: CreativeNameProps): ReactNode {
  const [draft, setDraft] = useState<string | null>(null);

  if (draft === null) {
    return (
      <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', minWidth: 0 }}>
        <Typography {...(variant === undefined ? {} : { variant })} sx={nameSx} noWrap>
          {name}
        </Typography>
        <IconButton size="small" aria-label={editLabel} onClick={() => setDraft(name)}>
          <EditOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
    );
  }

  const commit = (): void => {
    const next = draft.trim();
    if (next !== '' && next !== name) onRename(next);
    setDraft(null);
  };

  return (
    <TextField
      size="small"
      autoFocus
      fullWidth={fullWidth}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing) return;
        if (event.key === 'Enter') commit();
        if (event.key === 'Escape') setDraft(null);
      }}
      slotProps={{ htmlInput: { 'aria-label': 'Creative name' } }}
    />
  );
}
